import { useEffect } from 'react';
import { pickNext, type Direction } from './spatialNav.ts';

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),[role="tab"],[role="radio"],[role="switch"],[tabindex]:not([tabindex="-1"])';

const FIRST_REPEAT_MS = 380;
const REPEAT_MS = 130;
const STICK_THRESHOLD = 0.55;

function visibleFocusables(scope: ParentNode): HTMLElement[] {
  return [...scope.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => {
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0 && getComputedStyle(el).visibility !== 'hidden';
  });
}

function activeScope(): ParentNode {
  return document.querySelector('[aria-modal="true"]') ?? document;
}

function move(dir: Direction): void {
  const scope = activeScope();
  const items = visibleFocusables(scope);
  if (items.length === 0) return;
  const current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  if (!current || !items.includes(current)) {
    items[0]?.focus();
    return;
  }
  const next = pickNext(
    current.getBoundingClientRect(),
    items
      .filter((el) => el !== current)
      .map((el, id) => ({ id, rect: el.getBoundingClientRect() })),
    dir,
  );
  if (next === null) return;
  items.filter((el) => el !== current)[next]?.focus();
}

function directionFromPad(pad: Gamepad): Direction | null {
  const b = pad.buttons;
  if (b[12]?.pressed) return 'up';
  if (b[13]?.pressed) return 'down';
  if (b[14]?.pressed) return 'left';
  if (b[15]?.pressed) return 'right';
  const x = pad.axes[0] ?? 0;
  const y = pad.axes[1] ?? 0;
  if (Math.abs(x) < STICK_THRESHOLD && Math.abs(y) < STICK_THRESHOLD) return null;
  if (Math.abs(x) > Math.abs(y)) return x > 0 ? 'right' : 'left';
  return y > 0 ? 'down' : 'up';
}

/**
 * Polls connected gamepads: D-pad/left stick move focus, A (button 0) clicks the focused
 * element, B (button 1) sends Escape. Does nothing when no gamepad is connected.
 */
export function useGamepadNavigation(enabled = true): void {
  useEffect(() => {
    if (!enabled || typeof navigator.getGamepads !== 'function') return;
    let raf = 0;
    let heldDir: Direction | null = null;
    let nextRepeat = 0;
    let aHeld = false;
    let bHeld = false;

    const tick = (now: number) => {
      const pad = [...navigator.getGamepads()].find((p): p is Gamepad => p !== null);
      if (pad) {
        const dir = directionFromPad(pad);
        if (dir === null) {
          heldDir = null;
        } else if (dir !== heldDir || now >= nextRepeat) {
          nextRepeat = now + (dir === heldDir ? REPEAT_MS : FIRST_REPEAT_MS);
          heldDir = dir;
          move(dir);
        }
        const a = pad.buttons[0]?.pressed ?? false;
        if (a && !aHeld && document.activeElement instanceof HTMLElement)
          document.activeElement.click();
        aHeld = a;
        const b = pad.buttons[1]?.pressed ?? false;
        if (b && !bHeld) {
          (document.activeElement ?? document.body).dispatchEvent(
            new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', bubbles: true }),
          );
        }
        bHeld = b;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
    };
  }, [enabled]);
}
